import React, { useEffect, useRef, useState } from "react";

import {
  Button,
  DuotoneIcon,
  Heading,
  List,
  Notification,
  Span,
  Spinner,
  Upload,
} from "@icg360/design-system";

import { MSSDrawer } from "components/mss-drawer";
import { useKeystone, usePolicyId, useUser } from "data/ss-data-hooks";
import { isError } from "data/ss-error";
import { trackEvent } from "utils";

import styles from "./upload-drawer.module.scss";

const MAX_BYTE_FILE_SIZE = 30_000_000; // 30MB
const MAX_FILE_COUNT = 20;
export const FILE_COUNT_LIMIT_MESSAGE = "exceeds maximum of 20 files";
export const FILE_TOO_LARGE_MESSAGE = "file exceeds 30MB";

function getFileExtension(file: File): string {
  const name = file.name;
  const lastDot = name.lastIndexOf(".");
  if (lastDot === -1) return "file";
  return "." + name.slice(lastDot + 1).toLowerCase();
}

type FileRejection = {
  file: File;
  errors: { code: string; message: string }[];
};

export function getRejectionMessage(rejection: FileRejection): string {
  const code = rejection.errors.find(
    (e) => e.code === "file-invalid-type" || e.code === "file-too-large",
  )?.code;
  if (code === "file-invalid-type") {
    const ext = getFileExtension(rejection.file);
    return `${ext} is not an approved file type`;
  }
  if (code === "file-too-large") return FILE_TOO_LARGE_MESSAGE;
  return "File was rejected.";
}

export type UploadDrawerData = {
  inspectionId: string;
  issue: string;
  referralId: string;
  subHeading: string;
};

export const UploadDrawer = ({
  drawerOpen,
  onClose,
  onUploadSuccess,
  uploadData,
}: {
  drawerOpen: boolean;
  onClose: () => void;
  onUploadSuccess?: () => void;
  uploadData: UploadDrawerData | null;
}) => {
  const keystone = useKeystone();
  const insightPolicyId = usePolicyId();
  const user = useUser();
  const [uploads, setUploads] = useState<File[]>([]);
  const [validationErrors, setValidationErrors] = useState<
    { fileName: string; message: string }[]
  >([]);
  const [failedAssets, setFailedAssets] = useState<
    { fileName: string; message: string }[]
  >([]);
  const [isUploading, setIsUploading] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const uploadWrapperRef = useRef<HTMLDivElement>(null);
  // Used to force a re-render of the Upload component when the drawer is opened:
  const [uploadKey, setUploadKey] = useState(0);

  useEffect(() => {
    if (drawerOpen) {
      setUploads([]);
      setValidationErrors([]);
      setFailedAssets([]);
      setIsUploading(false);
      setSubmitError(false);
      setSubmitSuccess(false);
      setUploadKey((k) => k + 1);
    }
  }, [drawerOpen]);

  const onSubmit = async () => {
    const request = {
      insightPolicyId,
      inspectionId: uploadData?.inspectionId,
      referralId: uploadData?.referralId,
      userEmail: user?.email,
      userFullName: `${user?.firstName} ${user?.lastName}`.trim(),
    };

    try {
      setFailedAssets([]);
      setValidationErrors([]);
      setSubmitError(false);
      setSubmitSuccess(false);
      setIsUploading(true);
      const response = await keystone.uploadInspectionProof(request, uploads);

      if (!isError(response)) {
        if (response.failedAssets?.length) {
          setFailedAssets(
            response.failedAssets.map((a) => ({
              fileName: a.fileName,
              message: a.message,
            })),
          );
        } else {
          setUploads([]);
          setUploadKey((k) => k + 1);
          setSubmitSuccess(true);
          onUploadSuccess?.();
        }
      } else {
        setSubmitError(true);
      }
    } catch (_error) {
      setSubmitError(true);
    } finally {
      setIsUploading(false);
    }
  };

  const dropzoneOptions = {
    maxSize: MAX_BYTE_FILE_SIZE,
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
      "image/heic": [".heic"],
      "image/webp": [".webp"],
    },
    onDropRejected: (fileRejections: FileRejection[]) => {
      const { list, eventDetails } = fileRejections.reduce(
        (acc, r) => {
          const item = {
            fileName: r.file.name,
            message: getRejectionMessage(r),
          };
          acc.list.push(item);
          acc.eventDetails.push(`${item.fileName}: ${item.message}`);
          return acc;
        },
        {
          list: [] as { fileName: string; message: string }[],
          eventDetails: [] as string[],
        },
      );
      trackEvent("inspections-upload-drawer files rejected", {
        numberOfRejectedFiles: fileRejections.length,
        fileRejections: eventDetails,
      });
      setValidationErrors((prev) => [
        ...prev.filter((e) => e.message === FILE_COUNT_LIMIT_MESSAGE),
        ...list,
      ]);
    },
  };

  const handleUploadsChange = (files: File[]) => {
    if (files.length > MAX_FILE_COUNT) {
      trackEvent("inspections-upload-drawer files staged: over limit", {
        numberOfFiles: files.length,
        files: files.map((f) => `${f.name}: ${f.size} bytes`),
      });
      setUploads(files.slice(0, MAX_FILE_COUNT));
      const overLimit = files.slice(MAX_FILE_COUNT);
      setValidationErrors((prev) => [
        ...prev.filter((e) => e.message !== FILE_COUNT_LIMIT_MESSAGE),
        ...overLimit.map((f) => ({
          fileName: f.name,
          message: FILE_COUNT_LIMIT_MESSAGE,
        })),
      ]);
    } else {
      trackEvent("inspections-upload-drawer files staged", {
        files: files.map((f) => `${f.name}: ${f.size} bytes`),
      });
      setUploads(files);
      setValidationErrors((prev) =>
        prev.filter((e) => e.message !== FILE_COUNT_LIMIT_MESSAGE),
      );
    }
  };

  const loadingContent = (
    <div className={styles.uploadLoading}>
      <Spinner size="default" />
      <Heading size="xs">Please wait while we upload your files</Heading>
      <Span color="quiet">This may take a moment</Span>
    </div>
  );

  const errorContent = (
    <div className={styles.uploadError}>
      <DuotoneIcon size="lg" name="Error" />
      <Heading size="md" className={styles.errorHeading}>
        Your files could not be submitted
      </Heading>
      <Span color="quiet">
        Please try again or contact support if the issue persists
      </Span>
      <div className={styles.errorButtons}>
        <Button appearance="primary" onPress={() => setSubmitError(false)}>
          Try again
        </Button>
        <Button appearance="secondary" onPress={onClose}>
          Close
        </Button>
      </div>
    </div>
  );

  const successContent = (
    <div className={styles.uploadSuccess}>
      <DuotoneIcon size="lg" name="Done" />
      <Heading size="md" className={styles.successHeading}>
        We got your files
      </Heading>
      <Span color="quiet">We&apos;ll follow up within one business day.</Span>
      <Button className={styles.successButton} onPress={onClose}>
        Close
      </Button>
      <Button appearance="secondary" onPress={() => setSubmitSuccess(false)}>
        Add more files
      </Button>
    </div>
  );

  const uploadFormContent = (
    <>
      {(validationErrors.length > 0 || failedAssets.length > 0) && (
        <div className={styles.errorNotice}>
          <Notification
            appearance="danger"
            title="Upload failed for the following files:"
            description={
              <div className={styles.errorFileList}>
                <List type="ul">
                  {validationErrors.map((a) => (
                    <li key={a.fileName}>
                      <Span bold>&quot;{a.fileName}&quot;</Span>: {a.message}
                    </li>
                  ))}
                  {failedAssets.map((a) => (
                    <li key={a.fileName}>
                      <Span bold>&quot;{a.fileName}&quot;</Span>: {a.message}
                    </li>
                  ))}
                </List>
              </div>
            }
          />
        </div>
      )}
      <div className={styles.textGroup}>
        <Heading size="xs">Finding</Heading>
        <p style={{ fontWeight: "500" }}>Issue: {uploadData?.issue ?? ""}</p>
        <p className={styles.subHeading}>{uploadData?.subHeading ?? ""}</p>
      </div>
      <div className={styles.uploadPrompt}>
        <div className={styles.textGroup}>
          <p style={{ fontWeight: "500" }}>Upload your proof</p>
          <p className={styles.subHeading}>
            JPEG, PNG, HEIC, WEBP, DOC, DOCX, or PDF (max 30MB each)
          </p>
        </div>
        {uploads.length > 0 ? (
          <Button
            appearance="secondary"
            size="sm"
            leadingIcon="Add"
            className={styles.addMoreButton}
            onPress={() =>
              uploadWrapperRef.current
                ?.querySelector<HTMLInputElement>("input[type=file]")
                ?.click()
            }
          >
            Add more
          </Button>
        ) : null}
      </div>
      <div ref={uploadWrapperRef}>
        <Upload
          tiles
          fullDropZone
          key={uploadKey}
          mediaGridProps={{
            // Customize MediaTile size to better fit in drawer
            gridTemplateColumns: "repeat(auto-fit, minmax(135px, 160px))",
          }}
          dropzoneOptions={dropzoneOptions}
          onUploadsChange={handleUploadsChange}
        />
      </div>
    </>
  );

  let drawerBodyContent: React.ReactNode;
  if (isUploading) {
    drawerBodyContent = loadingContent;
  } else if (submitError) {
    drawerBodyContent = errorContent;
  } else if (submitSuccess) {
    drawerBodyContent = successContent;
  } else {
    drawerBodyContent = uploadFormContent;
  }

  const showFooter = !submitError && !submitSuccess;

  return (
    <MSSDrawer isOpen={drawerOpen} onClose={onClose}>
      <MSSDrawer.Header title="Resolve issue" onClose={onClose} />
      <MSSDrawer.Body className={styles.drawerBody}>
        {drawerBodyContent}
      </MSSDrawer.Body>
      {showFooter && (
        <MSSDrawer.Footer
          primaryLabel="Send"
          onPrimary={onSubmit}
          primaryDisabled={isUploading || uploads.length === 0}
          secondaryLabel="Cancel"
          secondaryDisabled={isUploading}
          onSecondary={onClose}
        />
      )}
    </MSSDrawer>
  );
};
